import { useEffect, useState, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import Underline from "@tiptap/extension-underline";
import SlashMenu from "@/components/SlashMenu";
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough, Code,
  Heading1, Heading2, Heading3, List, ListOrdered, ListChecks,
  Quote, Minus, Link as LinkIcon, Image as ImageIcon, Undo2, Redo2,
} from "lucide-react";

const BTN = "h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-accent/70 text-muted-foreground hover:text-foreground transition-colors";
const BTN_ACTIVE = "bg-primary/15 border border-primary/30 text-primary";

function Btn({ onClick, active, title, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      data-testid={testid}
      className={`${BTN} ${active ? BTN_ACTIVE : ""}`}
    >
      {children}
    </button>
  );
}

/**
 * TipTap-based block editor. Autosaves every 1.5s after the last keystroke.
 * `onChange(json)` is called with the current ProseMirror JSON — the parent
 * owns persistence (PATCH /documents/{id}).
 */
export default function DocumentEditor({ content, onChange, placeholder = "Start writing… press `/` for blocks", readOnly = false }) {
  const [savedAt, setSavedAt] = useState(null);
  const timerRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      Placeholder.configure({ placeholder }),
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { class: "text-primary underline underline-offset-2" } }),
      Image.configure({ HTMLAttributes: { class: "rounded-md max-w-full my-2" } }),
      TaskList,
      TaskItem.configure({ nested: true }),
    ],
    editable: !readOnly,
    content: content && Object.keys(content).length ? content : { type: "doc", content: [{ type: "paragraph" }] },
    editorProps: {
      attributes: {
        class: "prose prose-invert max-w-none focus:outline-none min-h-[400px] text-sm leading-relaxed",
      },
    },
    onUpdate: ({ editor }) => {
      // Debounced autosave — don't thrash the server on every keystroke.
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        onChange?.(editor.getJSON());
        setSavedAt(new Date());
      }, 1500);
    },
  });

  // When the parent swaps documents, blow away & re-set the content.
  useEffect(() => {
    if (!editor || !content) return;
    const current = editor.getJSON();
    const serialized = JSON.stringify(content);
    if (JSON.stringify(current) !== serialized) {
      editor.commands.setContent(content, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, editor]);

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  if (!editor) return <div className="text-muted-foreground text-sm">Loading editor…</div>;

  const promptLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Link URL", prev);
    if (url === null) return;
    if (!url) { editor.chain().focus().unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url, target: "_blank" }).run();
  };
  const promptImage = () => {
    const url = window.prompt("Image URL");
    if (!url) return;
    editor.chain().focus().setImage({ src: url }).run();
  };

  const groups = [
    [
      { icon: Bold,            cmd: () => editor.chain().focus().toggleBold().run(),           active: editor.isActive("bold"),         title: "Bold ⌘B",         testid: "fmt-bold" },
      { icon: Italic,          cmd: () => editor.chain().focus().toggleItalic().run(),         active: editor.isActive("italic"),       title: "Italic ⌘I",       testid: "fmt-italic" },
      { icon: UnderlineIcon,   cmd: () => editor.chain().focus().toggleUnderline().run(),      active: editor.isActive("underline"),    title: "Underline ⌘U",    testid: "fmt-underline" },
      { icon: Strikethrough,   cmd: () => editor.chain().focus().toggleStrike().run(),         active: editor.isActive("strike"),       title: "Strikethrough",   testid: "fmt-strike" },
      { icon: Code,            cmd: () => editor.chain().focus().toggleCode().run(),           active: editor.isActive("code"),         title: "Inline code",     testid: "fmt-code" },
    ],
    [
      { icon: Heading1,        cmd: () => editor.chain().focus().toggleHeading({ level: 1 }).run(), active: editor.isActive("heading", { level: 1 }), title: "Heading 1",   testid: "fmt-h1" },
      { icon: Heading2,        cmd: () => editor.chain().focus().toggleHeading({ level: 2 }).run(), active: editor.isActive("heading", { level: 2 }), title: "Heading 2",   testid: "fmt-h2" },
      { icon: Heading3,        cmd: () => editor.chain().focus().toggleHeading({ level: 3 }).run(), active: editor.isActive("heading", { level: 3 }), title: "Heading 3",   testid: "fmt-h3" },
    ],
    [
      { icon: List,            cmd: () => editor.chain().focus().toggleBulletList().run(),     active: editor.isActive("bulletList"),   title: "Bullet list",     testid: "fmt-ul" },
      { icon: ListOrdered,     cmd: () => editor.chain().focus().toggleOrderedList().run(),    active: editor.isActive("orderedList"),  title: "Numbered list",   testid: "fmt-ol" },
      { icon: ListChecks,      cmd: () => editor.chain().focus().toggleTaskList().run(),       active: editor.isActive("taskList"),     title: "Task list",       testid: "fmt-tasklist" },
      { icon: Quote,           cmd: () => editor.chain().focus().toggleBlockquote().run(),     active: editor.isActive("blockquote"),   title: "Quote",           testid: "fmt-quote" },
      { icon: Minus,           cmd: () => editor.chain().focus().setHorizontalRule().run(),    active: false,                           title: "Divider",         testid: "fmt-hr" },
    ],
    [
      { icon: LinkIcon,        cmd: promptLink,                                                 active: editor.isActive("link"),         title: "Link",            testid: "fmt-link" },
      { icon: ImageIcon,       cmd: promptImage,                                                active: false,                           title: "Image URL",       testid: "fmt-img" },
    ],
    [
      { icon: Undo2,           cmd: () => editor.chain().focus().undo().run(),                  active: false,                           title: "Undo",            testid: "fmt-undo" },
      { icon: Redo2,           cmd: () => editor.chain().focus().redo().run(),                  active: false,                           title: "Redo",            testid: "fmt-redo" },
    ],
  ];

  return (
    <div className="space-y-3">
      {!readOnly && (
        <div className="sticky top-16 z-10 flex items-center gap-1 flex-wrap p-1.5 rounded-lg glass-strong border" data-testid="doc-toolbar">
          {groups.map((group, gi) => (
            <div key={gi} className="flex items-center gap-0.5">
              {group.map((b) => (
                <Btn key={b.testid} onClick={b.cmd} active={b.active} title={b.title} testid={b.testid}>
                  <b.icon className="w-4 h-4" strokeWidth={1.8} />
                </Btn>
              ))}
              {gi < groups.length - 1 && <div className="h-5 w-px bg-border mx-1" />}
            </div>
          ))}
          <div className="ml-auto text-[10px] text-muted-foreground pr-2">
            {savedAt ? `Saved ${savedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : "All changes autosave"}
          </div>
        </div>
      )}
      <EditorContent editor={editor} data-testid="doc-editor" />
      <SlashMenu editor={editor} />
    </div>
  );
}
